import React, {
  FC, useEffect, useMemo, useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Mixpanel from 'mixpanel-browser';
import { LevelProps } from '~/core/dto/levelsDTO';
import { NavigationPath, ScreenProps } from '~/core/dto/navigationDTO';
import Quiz from '~/core/helpers/quiz';
import Range from '~/components/Range';
import PagerView from '~/components/PagerView';
import { APP_DOWNLOAD_LINK, MONTHLY_SUBSCRIPTION, YEARLY_SUBSCRIPTION } from '~/core/constants/data';
import reviews from '~/core/constants/reviews';
import Review from '~/components/Review';
import { changeThemeColor } from '~/core/helpers/internal';
import { AgeProps } from '~/core/dto/ageDTO';
import Spacer from '~/components/Spacer';

import rays from '~/assets/illustrations/Rays.svg';
import strong from '~/assets/images/strong.png';
import fire from '~/assets/images/fire.png';
import thumb from '~/assets/images/thumb.png';
import spotify from '~/assets/icons/Spotify.svg';
import reddit from '~/assets/icons/Reddit.svg';
import epicGames from '~/assets/icons/EpicGames.svg';
import qualia from '~/assets/images/qualia.png';
import mobileLesson from '~/assets/images/mobileLesson.png';
import mobilePortfolio from '~/assets/images/mobilePortfolio.png';
import mobileAcademy from '~/assets/images/mobileAcademy.png';
import mobileParent from '~/assets/images/mobileParent.png';
import mobileTicker from '~/assets/images/mobileTicker.png';
import mobileQuiz from '~/assets/images/mobileQuiz.png';
import check from '~/assets/images/check.png';
import euros from '~/assets/images/euros.png';
import lock from '~/assets/images/lock.png';
import suitcase from '~/assets/images/suitcase.png';
import wharton from '~/assets/icons/Wharton.svg';
import utrecht from '~/assets/icons/Utrecht.svg';
import kingsLondon from '~/assets/icons/KingsLondon.svg';
import forbes from '~/assets/icons/Forbes.svg';
import balance from '~/assets/images/balance.png';
import streak from '~/assets/images/streak.png';
import chart from '~/assets/illustrations/Chart.svg';
import birdwingo from '~/assets/icons/Birdwingo.svg';
import secure from '~/assets/icons/Secure.svg';
import payPal from '~/assets/icons/PayPal.svg';
import applePay from '~/assets/icons/ApplePay.svg';
import mastercard from '~/assets/icons/Mastercard.svg';
import visa from '~/assets/icons/Visa.svg';
import clock from '~/assets/icons/Clock.svg';
import lockIcon from '~/assets/icons/Lock.svg';
import stripe from '~/assets/icons/Stripe.svg';

import './QuizResult.styles.css';
import Header from '~/components/Header';

const ExpirationTime: FC<{ expiration: Date }> = ( { expiration } ) => {

  const [ timeLeft, setTimeLeft ] = useState<number>( expiration.getTime() - Date.now() );

  useEffect( () => {

    const interval = setInterval( () => {

      setTimeLeft( Math.max( 0, expiration.getTime() - Date.now() ) );

    }, 1000 );

    return () => clearInterval( interval );

  }, [] );

  return <span>{`${Math.floor( timeLeft / 1000 / 60 )}:${String( Math.floor( timeLeft / 1000 ) % 60 ).padStart( 2, '0' )}`}</span>;

};

const QuizResult: FC<ScreenProps> = ( { type } ) => {

  const navigate = useNavigate();
  const routeName = `QuizResult-${type}`;

  const [ data, setData ] = useState<{ level: LevelProps, age: AgeProps }>();
  const isParent = type === 'parent';
  const expiration = useMemo( () => new Date( Date.now() + 600000 ), [] );

  const openAppStore = () => {

    window.fbq( 'track', 'ViewContent', { content_name: 'AppStore' } );
    window.ttq.track( 'ViewContent', { contents: [ { content_id: 'AppStore', content_name: 'AppStore' } ] } );

    Mixpanel.track( routeName, { state: 1, type: 'DOWNLOAD' } );

    window.open( APP_DOWNLOAD_LINK, '_blank' );

  };

  const onPay = ( subscriptionType: 'YEARLY' | 'MONTHLY' = 'YEARLY' ) => {

    const price = subscriptionType === 'MONTHLY' ? 8.99 : 79.99;
    window.fbq( 'track', 'Purchase', { currency: 'EUR', value: price } );
    window.ttq.track( 'CompletePayment', { currency: 'EUR', value: price } );
    Mixpanel.track( routeName, { state: 1, type: subscriptionType } );

    window.open( subscriptionType === 'YEARLY' ? YEARLY_SUBSCRIPTION : MONTHLY_SUBSCRIPTION, '_blank' );

  };

  const onSubmitPayment = ( e: React.FormEvent<HTMLFormElement> ) => {

    e.preventDefault();

    const subscriptionType = ( e.currentTarget.querySelector( 'input[name="memberShip"]:checked' ) as HTMLInputElement ).value as 'YEARLY' | 'MONTHLY';

    onPay( subscriptionType );

  };

  useEffect( () => {

    Mixpanel.track( routeName, { state: 0 } );

    const newData = Quiz.getData( type );

    if ( !newData ) {

      navigate( NavigationPath.Home );

      return undefined;

    }

    setData( newData );

    changeThemeColor( '#FEFCF8' );

    return () => changeThemeColor( '#F1E8DC' );

  }, [] );

  if ( !data ) {

    return null;

  }

  const { level } = data;

  return (
    <div className={`quizResultContainer${isParent ? ' parent' : ''}`}>
      <div className="scrollContainer">
        {isParent ? (
          <>
            <div className="header discountHeader flexRow justifySpaceBetween alignCenter">
              <p>
                33% discount reserved for
                <br />
                <b><ExpirationTime expiration={expiration} /></b>
              </p>
              <div className="downloadAppBtn blinking" onClick={() => onPay()} aria-hidden>
                <p>Continue</p>
              </div>
            </div>
            <Header type="parent" top={60} />
          </>
        ) : (
          <div className="header flexRow justifySpaceBetween alignCenter">
            <Link className="birdwingoTitle" to={NavigationPath.AdultHome}>BIRDWINGO</Link>
            <div className="downloadAppBtn blinking" onClick={openAppStore} aria-hidden>
              <p>Download app</p>
            </div>
          </div>
        ) }
        <div className="quizResultPaddingContainer">
          {isParent ? (
            <section className="selectMembership flexColumn">
              <div className="selectMembershipHeader">
                <p>You’re eligible for Birdwingo’s exclusive club</p>
              </div>
              <div className="containerPadding flexColumn alignCenter">
                <Spacer size={8} />
                <h1 className="textCenter">Select a membership</h1>
                <Spacer size={24} />
                <form onSubmit={onSubmitPayment}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control  */}
                  <label className="radioButton flexRow alignCenter justifySpaceBetween">
                    <div className="flexRow alignCenter">
                      <input type="radio" name="memberShip" value="MONTHLY" />
                      <Spacer size={8} horizontal />
                      <div>
                        <div>
                          <p>Monthly</p>
                          <Spacer size={4} />
                          <p>
                            <span className="oldPrice">€13.99</span>
                          &#160;
                            <span className="newPrice">€8.99</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flexRow">
                      <p className="euro">€</p>
                      <p className="zero">0</p>
                      <div>
                        <p className="euro">30</p>
                        <Spacer size={2} />
                        <p className="perDay">PER DAY</p>
                      </div>
                    </div>
                  </label>
                  <Spacer size={16} />
                  <div className="popular flexColumn">
                    <p className="popularTitle textCenter">MOST POPULAR</p>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control  */}
                    <label className="radioButton flexRow alignCenter justifySpaceBetween">
                      <div className="flexRow alignCenter">
                        <input type="radio" name="memberShip" value="YEARLY" defaultChecked />
                        <Spacer size={8} horizontal />
                        <div>
                          <div>
                            <p>Yearly</p>
                            <Spacer size={4} />
                            <p>
                              <span className="oldPrice">€119.99</span>
                          &#160;
                              <span className="newPrice">€79.99</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flexRow">
                        <p className="euro">€</p>
                        <p className="zero">0</p>
                        <div>
                          <p className="euro">22</p>
                          <Spacer size={2} />
                          <p className="perDay">PER DAY</p>
                        </div>
                      </div>
                    </label>
                  </div>
                  <Spacer size={24} />
                  <p className="textCenter">You will then be emailed your access code</p>
                  <button type="submit" className="downloadAppBtn blinking">
                    <p>Become a member</p>
                  </button>
                </form>
                <Spacer size={24} />
                <div className="paySecure flexRow alignCenter">
                  <img src={secure} alt="" />
                  <Spacer size={4} horizontal />
                  <p>Pay safe & secure</p>
                </div>
                <Spacer size={24} />
                <div className="flexRow alignCenter justifyCenter">
                  <img src={payPal} alt="PayPal" />
                  <Spacer size={8} horizontal />
                  <img src={applePay} alt="ApplePay" />
                  <Spacer size={8} horizontal />
                  <img src={mastercard} alt="Mastercard" />
                  <Spacer size={8} horizontal />
                  <img src={visa} alt="Visa" />
                </div>
                <Spacer size={16} />
              </div>
            </section>
          ) : (
            <section className="quizResults flexColumn alignCenter">
              <div className="quizResultsContainer flexColumn alignCenter">
                <img className="quizResultsRays" src={rays} alt="" />
                <h1 className="textCenter">Your Results are ready!</h1>
                <Spacer size={24} />
                <img className="levelIcon" src={level.icon} alt={level.name} />
                <Spacer size={24} />
                <div className="levelResult flexColumn alignCenter">
                  <p>You are</p>
                  <h1 className="textCenter">{level.name}</h1>
                </div>
                <Spacer size={16} />
              </div>
              <div className="quizResultsContainer flexColumn alignCenter">
                <p className="textCenter levelDescription">{level.description}</p>
                <Spacer size={48} />
                <div className="levelRankContainer flexRow alignCenter">
                  <img src={strong} alt="" />
                  <p>
                    You ranked better than&#160;
                    <b>{`${level.rank}%`}</b>
                    &#160;of people.
                  </p>
                </div>
                <div className="downloadAppBtn blinking" onClick={openAppStore} aria-hidden>
                  <p>Get even better</p>
                </div>
              </div>
            </section>
          )}
          <Spacer size={16} />
          <section className="ageResult flexColumn alignCenter">
            <h1 className="textCenter">
              You’re set for a great start. Birdwingo could be your tool&#160;
              <img src={fire} alt="" />
            </h1>
            <Spacer size={16} />
            <div className="progressComparison flexRow alignCenter">
              <p className="textCenter">Now</p>
              <p className="textCenter">In 3 months</p>
            </div>
            <img className="ageImage" src={data.age.finalImage} alt="" />
            <div className="completeResults">
              <div className="completeResultItem flexColumn">
                <h3>Financial skills</h3>
                <Range
                  progress={level.progressNow}
                  isGradient
                  label={level.financialSkillsNow}
                />
              </div>
              <div className="completeResultItem flexColumn">
                <h3>Financial skills</h3>
                <Range
                  progress={level.progress3months}
                  isGradient
                  label={level.financialSkills3months}
                />
              </div>
              <div className="completeResultItem flexColumn">
                <h3>Savings & Investing</h3>
                <Range progress={0.2} />
                <div className="flexRow alignCenter savings savingsNow">
                  <img src={thumb} alt="" />
                  <p>A little</p>
                </div>
              </div>
              <div className="completeResultItem flexColumn">
                <h3>Savings & Investing</h3>
                <Range progress={0.7} />
                <div className="flexRow alignCenter savings">
                  <img src={thumb} alt="" />
                  <p>Regularly</p>
                </div>
              </div>
            </div>
          </section>
          <Spacer size={16} />
          {isParent && (
            <>
              <section className="moneyRewards flexColumn">
                <h1 className="textCenter">
                  In Birdwingo kids earn&#160;
                  <span>money rewards</span>
                  &#160;for learning
                </h1>
                <div className="rewardNotifications containerPadding flexColumn">
                  <div className="rewardNotification">
                    <div className="imageContainer">
                      <img src={balance} alt="" />
                    </div>
                    <p>You just got 2.50€ for completing &quot;Budgeting&quot; lesson</p>
                  </div>
                  <div className="rewardNotification">
                    <div className="imageContainer">
                      <img src={streak} alt="" />
                    </div>
                    <p>You just got 1.50€ for her 6 day streak!</p>
                  </div>
                </div>
                <div className="containerPadding flexColumn">
                  <h2 className="textCenter">Birdwingo will help your child to</h2>
                  <Spacer size={24} />
                  <div className="checkItem">
                    <img src={check} alt="" />
                    <p>{`Manage ${isParent ? 'their' : 'your'} finances better`}</p>
                  </div>
                  <div className="checkItem">
                    <img src={check} alt="" />
                    <p>Understand how investing works</p>
                  </div>
                  <div className="checkItem">
                    <img src={check} alt="" />
                    <p>Apply theory in practice, in a controlled environment</p>
                  </div>
                </div>
              </section>
              <Spacer size={16} />
            </>
          )}
          <section className="downloadNow flexColumn">
            {isParent ? (
              <p className="financialLiteracy">
                <b>Only 10%</b>
                &#160;of EU students are proficient in&#160;
                <b>financial literacy.</b>
                <br />
                <br />
                Help your child be prepared for the future.
              </p>
            ) : (
              <p className="financialLiteracy">
                <b>Only 18%</b>
                &#160;of EU adults are proficient in&#160;
                <b>financial literacy.</b>
                <br />
                <br />
                Become prepared for the future.
              </p>
            )}
            <Spacer size={16} />
            <p className="source">{isParent ? 'Source: OECD' : 'Source: European Commission'}</p>
            {!isParent ? (
              <>
                <Spacer size={32} />
                <div className="downloadAppBtn blinking" onClick={openAppStore} aria-hidden>
                  <p>Download now</p>
                </div>
              </>
            ) : <Spacer size={16} />}
          </section>
          <Spacer size={16} />
          {isParent ? (
            <section className="after30Days flexColumn">
              <div>
                <h2>94%</h2>
                <p>Report better money skills</p>
              </div>
              <div>
                <h2>79%</h2>
                <p>Started saving & investing</p>
              </div>
            </section>
          ) : (
            <section className="investors flexColumn">
              <h1 className="textCenter">From the investors behind</h1>
              <Spacer size={24} />
              <ul className="investorsList flexRow justifyCenter alignCenter">
                <li>
                  <img src={spotify} alt="Spotify" />
                </li>
                <li>
                  <img src={reddit} alt="Reddit" />
                </li>
                <li>
                  <img src={epicGames} alt="Epic Games" />
                </li>
                <li>
                  <img src={qualia} alt="Qualia" />
                </li>
              </ul>
              <img className="mobileLesson" src={mobileLesson} alt="" />
            </section>
          )}
          <Spacer size={16} />
          <section className="tryBirdwingo flexColumn">
            {!isParent && (
              <>
                <h1 className="textCenter">
                  Try Birdwingo
                  <br />
                  & you will
                </h1>
                <Spacer size={24} />
                <div className="checkItem">
                  <img src={check} alt="" />
                  <p>{`Manage ${isParent ? 'their' : 'your'} finances better`}</p>
                </div>
                <div className="checkItem">
                  <img src={check} alt="" />
                  <p>Understand how investing works</p>
                </div>
                <div className="checkItem">
                  <img src={check} alt="" />
                  <p>Apply theory in practice, in a controlled environment</p>
                </div>
                <div className="downloadAppBtn blinking" onClick={openAppStore} aria-hidden>
                  <p>Try it now</p>
                </div>
              </>
            )}
            <h2>Starting is simple</h2>
            <div className="startingItems">
              <div className="startingItem">
                <div className="startingItemImage">
                  <img src={euros} alt="" />
                </div>
                <p>You can start with 1€</p>
              </div>
              <div className="startingItem">
                <div className="startingItemImage">
                  <img src={strong} alt="" />
                </div>
                <p>Take it step by step</p>
              </div>
              <div className="startingItem">
                <div className="startingItemImage">
                  <img src={lock} alt="" />
                </div>
                <p>{isParent ? 'Controlled environment & full parental controls' : 'No need to be market expert'}</p>
              </div>
            </div>
            <img className="mobileAcademy" src={mobileAcademy} alt="" />
          </section>
          <Spacer size={16} />
          {!isParent && (
            <section className="idealInvestmentsMix flexColumn">
              <img className="suitcase" src={suitcase} alt="" />
              <Spacer size={24} />
              <h1 className="textCenter">
                Your ideal
                <br />
                investment mix
              </h1>
              <Spacer size={24} />
              <div className="checkItem">
                <img src={check} alt="" />
                <p>85% High-performing ETFs</p>
              </div>
              <div className="checkItem">
                <img src={check} alt="" />
                <p>10% Individual Stocks of your favourite companies</p>
              </div>
              <div className="checkItem">
                <img src={check} alt="" />
                <p>5% Government Bonds</p>
              </div>
              <Spacer size={8} />
              <p className="info">* Don’t know what these are? No worries! It’ll all become very easy soon.</p>
              <Spacer size={32} />
              <h1 className="textCenter">If you start today</h1>
              <Spacer size={32} />
              <p>
                If you start today & invest 100€ monthly, you can get to more than&#160;
                <b>230.000€</b>
                &#160;in 30 years
                <br />
                <br />
                If you keep that money in the bank, it will be&#160;
                <b>36 000€</b>
                .
              </p>
              <div className="downloadAppBtn blinking" onClick={openAppStore} aria-hidden>
                <p>Try it now</p>
              </div>
            </section>
          )}
          <Spacer size={16} />
          {isParent ? (
            <section className="investors flexColumn">
              <h1 className="textCenter">From the investors behind</h1>
              <Spacer size={24} />
              <ul className="investorsList flexRow justifyCenter alignCenter">
                <li>
                  <img src={spotify} alt="Spotify" />
                </li>
                <li>
                  <img src={reddit} alt="Reddit" />
                </li>
                <li>
                  <img src={epicGames} alt="Epic Games" />
                </li>
                <li>
                  <img src={qualia} alt="Qualia" />
                </li>
              </ul>
              <img className="mobileLesson" src={mobileLesson} alt="" />
            </section>
          ) : (
            <section className="after30Days flexColumn">
              <h1 className="textCenter">After 30 days of using Birdwingo</h1>
              <div>
                <h2 className="textCenter">94%</h2>
                <p className="textCenter">Report better money skills</p>
              </div>
              <div>
                <h2 className="textCenter">79%</h2>
                <p className="textCenter">Started saving & investing</p>
              </div>
            </section>
          )}
          {!isParent && (
          <>
            <Spacer size={16} />
            <section className="premiumAcademy">
              <h1 className="textCenter">
                Premium Academy&#160;
                <span className="primaryBroadbill">created by</span>
              &#160;Top Notch&#160;
                <span className="primaryBroadbill">Ivy League Experts</span>
              </h1>
              <Spacer size={48} />
              <div className="universities flexColumn alignCenter">
                <img src={wharton} alt="Wharton" />
                <img src={utrecht} alt="Utrecht" />
                <img src={kingsLondon} alt="Kings London" />
                <div>
                  <Spacer size={16} />
                  <p className="textCenter">MENTIONED IN</p>
                  <Spacer size={16} />
                  <img src={forbes} alt="Forbes" />
                </div>
              </div>
            </section>
            <Spacer size={16} />
            <section className="moneyRewards flexColumn">
              <h1 className="textCenter">
                In Birdwingo you earn&#160;
                <span>money rewards</span>
                &#160;for learning
              </h1>
              <div className="rewardNotifications containerPadding flexColumn">
                <div className="rewardNotification">
                  <div className="imageContainer">
                    <img src={balance} alt="" />
                  </div>
                  <p>You just got 2.50€ for completing &quot;Budgeting&quot; lesson</p>
                </div>
                <div className="rewardNotification">
                  <div className="imageContainer">
                    <img src={streak} alt="" />
                  </div>
                  <p>You just got 1.50€ for her 6 day streak!</p>
                </div>
              </div>
            </section>
          </>
          )}
          <Spacer size={16} />
          <section className="reviews flexColumn">
            <h1 className="textCenter">20 000+ users love the Birdwingo app</h1>
            <Spacer size={16} />
            <div className="reviewsList flexColumn">
              {reviews.map( ( review, index ) => <Review key={`review${String( index )}`} {...review} /> )}
            </div>
          </section>
          <Spacer size={16} />
          <section className="getBetter">
            <h1>
              Birdwingo users get&#160;
              <span>70% better</span>
              &#160;at understanding the market and investing compared to others
            </h1>
            <Spacer size={48} />
            <img src={chart} alt="" />
          </section>
        </div>
        <Spacer size={16} />
        <div className={isParent ? 'quizResultPaddingContainer' : ''}>
          <section className="keepBirdwingo flexColumn">
            <div className="keepBirdwingoContent flexColumn alignCenter">
              <img src={birdwingo} alt="Birdwingo" />
              <h1 className="textCenter">Keep Birdwingo always with you</h1>
            </div>
            <div className="mobilePagerView">
              <PagerView highlightActive useArrows autoScroll={false} defaultIndex={2}>
                <img className="mobile" src={mobilePortfolio} alt="" />
                <img className="mobile" src={mobileAcademy} alt="" />
                <img className="mobile" src={mobileParent} alt="" />
                <img className="mobile" src={mobileTicker} alt="" />
                <img className="mobile" src={mobileQuiz} alt="" />
              </PagerView>
            </div>
            {!isParent && (
            <div className="keepBirdwingoBtn">
              <div className="downloadAppBtn blinking" onClick={openAppStore} aria-hidden>
                <p>Download now</p>
              </div>
            </div>
            )}
          </section>
        </div>
        {isParent && (
          <>
            <Spacer size={16} />
            <div className="quizResultPaddingContainer">
              <section className="selectMembership containerPadding flexColumn alignCenter">
                <Spacer size={8} />
                <h1 className="textCenter">Become a member</h1>
                <Spacer size={24} />
                <div className="clock flexRow alignCenter">
                  <img src={clock} alt="" />
                  <Spacer size={4} horizontal />
                  <p>
                    33% discount expires in&#160;
                    <ExpirationTime expiration={expiration} />
                  </p>
                </div>
                <Spacer size={24} />
                <form onSubmit={onSubmitPayment}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control  */}
                  <label className="radioButton flexRow alignCenter justifySpaceBetween">
                    <div className="flexRow alignCenter">
                      <input type="radio" name="memberShip" value="MONTHLY" />
                      <Spacer size={8} horizontal />
                      <div>
                        <div>
                          <p>Monthly</p>
                          <Spacer size={4} />
                          <p>
                            <span className="oldPrice">€13.99</span>
                          &#160;
                            <span className="newPrice">€8.99</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flexRow">
                      <p className="euro">€</p>
                      <p className="zero">0</p>
                      <div>
                        <p className="euro">30</p>
                        <Spacer size={2} />
                        <p className="perDay">PER DAY</p>
                      </div>
                    </div>
                  </label>
                  <Spacer size={16} />
                  <div className="popular flexColumn">
                    <p className="popularTitle textCenter">MOST POPULAR</p>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control  */}
                    <label className="radioButton flexRow alignCenter justifySpaceBetween">
                      <div className="flexRow alignCenter">
                        <input type="radio" name="memberShip" value="YEARLY" defaultChecked />
                        <Spacer size={8} horizontal />
                        <div>
                          <div>
                            <p>Yearly</p>
                            <Spacer size={4} />
                            <p>
                              <span className="oldPrice">€119.99</span>
                          &#160;
                              <span className="newPrice">€79.99</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flexRow">
                        <p className="euro">€</p>
                        <p className="zero">0</p>
                        <div>
                          <p className="euro">22</p>
                          <Spacer size={2} />
                          <p className="perDay">PER DAY</p>
                        </div>
                      </div>
                    </label>
                  </div>
                  <Spacer size={24} />
                  <p className="textCenter">You will then be emailed your access code</p>
                  <button type="submit" className="downloadAppBtn blinking">
                    <img src={lockIcon} alt="" />
                    <Spacer size={8} horizontal />
                    <p>Let’s do this</p>
                  </button>
                </form>
                <Spacer size={24} />
                <div className="paySecure flexRow alignCenter">
                  <img src={secure} alt="" />
                  <Spacer size={4} horizontal />
                  <p>Pay safe & secure</p>
                </div>
                <Spacer size={24} />
                <div className="poweredStripe flexRow alignCenter">
                  <p>Powered by</p>
                  <Spacer size={4} horizontal />
                  <img src={stripe} alt="" />
                </div>
                <Spacer size={24} />
                <div className="flexRow alignCenter justifyCenter">
                  <img src={payPal} alt="PayPal" />
                  <Spacer size={8} horizontal />
                  <img src={applePay} alt="ApplePay" />
                  <Spacer size={8} horizontal />
                  <img src={mastercard} alt="Mastercard" />
                  <Spacer size={8} horizontal />
                  <img src={visa} alt="Visa" />
                </div>
                <Spacer size={16} />
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );

};

export default QuizResult;
